import { createSlice } from '@reduxjs/toolkit';

import eventBus, { EventType } from '@utils/eventBus';

import {
  handleAdminActivateOrganization,
  handleAdminCreateNewOrganization,
  handleAdminDeactivateOrganization,
  handleAdminEditOrganization,
  handleAdminSearchOrganizations,
  handleGetAdminOrganizationDetails,
} from './actions';
import { IAdminOrganizationsState } from './slice.interface';
import { updateOrganizationDetailsIsActive, updateOrganizationInListIsActive } from './utils';

const initialState: IAdminOrganizationsState = {
  data: null,
  isLoading: false,
  error: false,
  organizationId: null,
  organization: null,
  organizationIsLoading: false,
  organizationError: false,
  isActivatingOrDeactivating: false,
  isCreatingNew: false,
};

export const adminOrganizationsSlice = createSlice({
  name: 'adminOrganizations',
  initialState,
  reducers: {
    removeAdminOrganizationList: state => {
      state.data = null;
      state.isLoading = false;
      state.error = false;
    },
    removeAdminOrganizationDetails: state => {
      state.organizationId = null;
      state.organization = null;
      state.organizationIsLoading = false;
      state.organizationError = false;
      state.isActivatingOrDeactivating = false;
      state.isCreatingNew = false;
    },
    setAdminOrganizationCreatingNew: state => {
      state.organizationId = null;
      state.organization = null;
      state.organizationIsLoading = false;
      state.organizationError = false;
      state.isActivatingOrDeactivating = false;
      state.isCreatingNew = true;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(handleAdminSearchOrganizations.pending, (state: IAdminOrganizationsState) => {
        state.data = null;
        state.isLoading = true;
        state.error = false;
      })
      .addCase(handleAdminSearchOrganizations.rejected, (state: IAdminOrganizationsState) => {
        state.isLoading = false;
        state.error = true;
      })
      .addCase(
        handleAdminSearchOrganizations.fulfilled,
        (state: IAdminOrganizationsState, action) => {
          state.data = action.payload.data;
          state.isLoading = false;
        },
      )
      .addCase(
        handleGetAdminOrganizationDetails.pending,
        (state: IAdminOrganizationsState, action) => {
          state.organizationId = action.meta.arg;
          state.organizationIsLoading = true;
          state.organizationError = false;
          state.isActivatingOrDeactivating = false;
          state.isCreatingNew = false;
        },
      )
      .addCase(handleGetAdminOrganizationDetails.rejected, (state: IAdminOrganizationsState) => {
        state.organizationIsLoading = false;
        state.organizationError = true;
      })
      .addCase(
        handleGetAdminOrganizationDetails.fulfilled,
        (state: IAdminOrganizationsState, action) => {
          state.organization = action.payload.data;
          state.organizationIsLoading = false;
        },
      )
      .addCase(handleAdminCreateNewOrganization.fulfilled, () => {
        eventBus.emit(EventType.ADMIN_CREATE_NEW_ORGANIZATION_SUCCESS);
      })
      .addCase(handleAdminEditOrganization.fulfilled, () => {
        eventBus.emit(EventType.ADMIN_EDIT_ORGANIZATION_SUCCESS);
      })
      .addCase(handleAdminActivateOrganization.pending, (state: IAdminOrganizationsState) => {
        state.isActivatingOrDeactivating = true;
      })
      .addCase(handleAdminActivateOrganization.rejected, (state: IAdminOrganizationsState) => {
        state.isActivatingOrDeactivating = false;
      })
      .addCase(handleAdminActivateOrganization.fulfilled, (state: IAdminOrganizationsState) => {
        if (state.data && state.organization) {
          state.data = updateOrganizationInListIsActive(state.data, state.organization.id, true);
          state.organization = updateOrganizationDetailsIsActive(state.organization, true);
          state.isActivatingOrDeactivating = false;
        }
        eventBus.emit(EventType.ADMIN_ACTIVATE_ORGANIZATION_SUCCESS);
      })
      .addCase(handleAdminDeactivateOrganization.pending, (state: IAdminOrganizationsState) => {
        state.isActivatingOrDeactivating = true;
      })
      .addCase(handleAdminDeactivateOrganization.rejected, (state: IAdminOrganizationsState) => {
        state.isActivatingOrDeactivating = false;
      })
      .addCase(handleAdminDeactivateOrganization.fulfilled, (state: IAdminOrganizationsState) => {
        if (state.data && state.organization) {
          state.data = updateOrganizationInListIsActive(state.data, state.organization.id, false);
          state.organization = updateOrganizationDetailsIsActive(state.organization, false);
          state.isActivatingOrDeactivating = false;
        }
        eventBus.emit(EventType.ADMIN_DEACTIVATE_ORGANIZATION_SUCCESS);
      });
  },
});

export const {
  removeAdminOrganizationDetails,
  removeAdminOrganizationList,
  setAdminOrganizationCreatingNew,
} = adminOrganizationsSlice.actions;

export default adminOrganizationsSlice.reducer;
